import styled from 'styled-components';

import Text from 'common/typography/Text/Text';
import Loading from '../../../../../common/components/atoms/Loading';

export const hexToRgba = (hex, alpha) => {
  if (!hex || typeof hex !== 'string') {
    return `rgba(56, 56, 56, ${alpha})`;
  }
  const normalized = hex.replace('#', '');
  if (normalized.length !== 6) {
    return `rgba(56, 56, 56, ${alpha})`;
  }
  const r = Number.parseInt(normalized.substring(0, 2), 16);
  const g = Number.parseInt(normalized.substring(2, 4), 16);
  const b = Number.parseInt(normalized.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const TrendChartContainer = styled.div`
  position: relative;
  ${props => (props.optionsVisible ? 'width: calc(100% - 220px);' : 'width: 100%;')};
  ${props => (props.chartHeight ? `min-height: ${props.chartHeight};` : '')};
`;

export const AbsoluteLoading = styled(Loading)`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #e4e4e447;
  z-index: 2;
`;

export const OverlayLoadingMessage = styled(Text)`
  margin-top: 8px;
  font-size: 0.85em;
  color: ${props => props.theme.colors.darkGray || '#555'};
`;

export const SpectrumLegendContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 4px auto 12px auto;
  width: 100%;
  padding: 0 16px;
`;

export const SpectrumLegend = styled.div`
  margin-top: 8px;
  width: auto;
  max-width: 100%;
  background: ${props => props.theme.colors.white};
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.colors.greyL || props.theme.colors.lightGray};
  display: inline-flex;
  flex-wrap: nowrap;
  /*
   * Must be flex-start, not center: this element is the horizontal scroll container
   * (overflow-x: auto). With justify-content: center, once the cards overflow they are
   * centered and spill off BOTH ends, and the browser cannot scroll to the start, so the
   * overflowing cards become unreachable (only the ~14 of 20 that fit were ever visible).
   * flex-start keeps the overflow on the trailing edge so every card scrolls into view.
   * Centering when the cards fit is still handled by the parent SpectrumLegendContainer
   * (justify-content: center) since this element is width: auto / max-width: 100%.
   */
  justify-content: flex-start;
  gap: 6px;
  overflow-x: auto;
  overflow-y: hidden;
  box-shadow: 0 1px 4px rgba(56, 56, 56, 0.08);

  ${props => props.theme.scrollbar && props.theme.scrollbar}
`;

export const LegendItem = styled.div`
  display: flex;
  flex-direction: ${props => (props.multiLine ? 'column' : 'row')};
  align-items: ${props => (props.multiLine ? 'stretch' : 'center')};
  position: relative;
  margin: 0;
  padding: 8px 10px 8px 12px;
  border-radius: 6px;
  border: 1px solid ${props => props.theme.colors.greyL || props.theme.colors.lightGray};
  transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
  cursor: pointer;
  font-size: 12px;
  line-height: 1.35;
  flex: 0 0 auto;
  min-width: 148px;
  max-width: 220px;
  background-color: ${props => props.theme.colors.greyXXXL || '#F7F7F7'};
  box-shadow: inset 3px 0 0 ${props => props.traceColor || props.theme.colors.green};
  opacity: ${(props) => {
    if (props.isActive || props.isHovered) return 1;
    return props.dimmed ? 0.55 : 1;
  }};

  ${props => (props.isActive || props.isHovered) && `
    background-color: ${hexToRgba(props.traceColor, 0.1)};
    border-color: ${hexToRgba(props.traceColor, 0.45)};
    box-shadow: inset 3px 0 0 ${props.traceColor}, 0 2px 6px ${hexToRgba(props.traceColor, 0.18)};
  `}

  &:hover,
  &:focus-within {
    background-color: ${props => hexToRgba(props.traceColor, props.isActive ? 0.14 : 0.08)};
    border-color: ${props => hexToRgba(props.traceColor, 0.4)};
    opacity: 1;
  }

  &:focus {
    outline: 2px solid ${props => hexToRgba(props.traceColor, 0.5)};
    outline-offset: 1px;
  }
`;

export const ColorIndicator = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => props.color};
  margin-right: 8px;
  flex-shrink: 0;
  box-shadow: 0 0 0 2px ${props => hexToRgba(props.color, 0.2)};
`;

export const CloseIcon = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 4px;
  background-color: ${props => props.theme.colors.greyXL || '#EFF0EE'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 4px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s ease, background-color 0.15s ease;
  position: relative;

  ${LegendItem}:hover &,
  ${LegendItem}:focus-within & {
    opacity: 1;
  }

  &:hover {
    background-color: ${props => props.theme.colors.greyL || '#E5E8E1'};
  }

  &:before, &:after {
    content: '';
    position: absolute;
    width: 8px;
    height: 2px;
    background-color: #444;
  }

  &:before {
    transform: rotate(45deg);
  }

  &:after {
    transform: rotate(-45deg);
  }
`;

export const EditIcon = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 4px;
  background-color: ${props => props.theme.colors.greyXL || '#EFF0EE'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 4px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s ease, background-color 0.15s ease;
  position: relative;

  ${LegendItem}:hover &,
  ${LegendItem}:focus-within & {
    opacity: 1;
  }

  &:hover {
    background-color: ${props => props.theme.colors.greyL || '#E5E8E1'};
  }
`;

export const DescriptionInput = styled.input`
  margin-left: 4px;
  padding: 2px 4px;
  border: 1px solid #ccc;
  border-radius: 3px;
  font-size: 0.85em;
  width: 120px;
  max-width: 120px;
  &[type="text"] {
    max-length: 50;
  }
`;

export const LegendHeader = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  justify-content: space-between;
  gap: 6px;
`;

export const LegendMain = styled.div`
  display: flex;
  align-items: flex-start;
  min-width: 0;
  flex: 1;
`;

export const LegendContent = styled.div`
  margin-top: 4px;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

export const LegendDate = styled.div`
  font-weight: 600;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${props => props.theme.colors.black || '#383838'};
  letter-spacing: 0.01em;
`;

export const LegendInfo = styled.div`
  color: ${props => props.theme.colors.greyXD || '#999B95'};
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const SpeedValue = styled.span`
  font-weight: 600;
  color: ${props => props.theme.colors.black || '#383838'};
  font-variant-numeric: tabular-nums;
`;

export const SpeedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  margin-top: 4px;
  padding: 2px 6px;
  border-radius: 4px;
  background: ${props => props.theme.colors.white};
  border: 1px solid ${props => props.theme.colors.greyL || '#E5E8E1'};
  font-size: 11px;
  width: fit-content;
`;

export const SpeedLabel = styled.span`
  color: ${props => props.theme.colors.greyXD || '#999B95'};
  margin-right: 4px;
`;

export const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-top: -2px;
`;

export const DescriptionText = styled.span`
  color: ${props => props.theme.colors.greyXD || '#999B95'};
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
  font-style: ${props => (props.isEmpty ? 'italic' : 'normal')};
  font-size: 11px;
  line-height: 1.3;
`;
